import {
  BorrowerMinimalLegalEntityDto,
  BorrowerMinimalNaturalPersonDto,
  BorrowerMinimalPersonDto
} from 'api/borrower';
import { ReferentialKey } from 'api/referential';
import { Button } from 'components/ui/Button';
import { GenericCardInfos } from 'components/display/Card/CardInfos';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay';
import { CommercialIcon, ManIcon, WomanIcon } from 'components/icons/customIcons';
import { PenIcon, TrashIcon } from 'components/icons/Icons';
import { ActorType } from 'features/new-actor/enums/actorType';
import { selectReferentialDataByKey } from 'features/referentials/selectors/selectReferentialDataByKey';
import { calculateAge } from 'helpers/calculateAge';
import { formatDateToFR } from 'helpers/convertDateToFR';
import { isLegalEntity } from 'helpers/isLegalEntity';
import { FC, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

interface BorrowerInsuredCardProps {
  person: BorrowerMinimalPersonDto;
  onUpdate: (personId: string) => void;
  onDelete?: (personId: string) => void;
}

export const BorrowerInsuredCard: FC<BorrowerInsuredCardProps> = ({
  person,
  onUpdate,
  onDelete
}) => {
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const civilityReferential = useSelector(selectReferentialDataByKey(ReferentialKey.Civility))
  const getCivility = useCallback((civilityId: string) => {
    const civilityRef = civilityReferential.find(ref => ref.id === civilityId)
    return civilityRef ? civilityRef.code : ''
  }, [civilityReferential])

  const mapIcon = useCallback((key: string) => {
    switch (key) {
      case "Mr":
        return <ManIcon />
      case "Mme":
        return <WomanIcon />
      case ActorType.Legal:
        return <CommercialIcon className="text-primary w-6" />
    }
  }, [])

  const isMan = useCallback((civility: string) => {
    return civility === "Mr"
  }, [])

  const renderBody = useMemo(() => {
    if (isLegalEntity(person)) {
      const legalEntity = person as BorrowerMinimalLegalEntityDto
      return (
        <>
          <div className="flex gap-2 items-center">
            {mapIcon(ActorType.Legal)}
            <p className="font-bold flex-none">
              {`${legalEntity.companyName}`}
            </p>
          </div>
          <p>{` ${legalEntity.registrationNumber}`}</p>
          <p className="text-sm font-light">{person.email}</p>
          <p className="text-sm font-light">{person.phone}</p>
        </>
      )
    }
    const naturalPerson = person as BorrowerMinimalNaturalPersonDto
    const civility = getCivility(naturalPerson.civilityId)
    const birthDate = new Date(naturalPerson.birthDate)
    return <>
      <div className="flex gap-2 items-center">
        {mapIcon(civility)}
        <p className="font-bold flex-none">
          {`${civility} ${naturalPerson.lastName.toUpperCase()} ${naturalPerson.firstName}`}
        </p>
      </div>
      {
        naturalPerson.birthDate &&
        <p>{`Né${isMan(civility) ? "" : "e"} le ${formatDateToFR(birthDate)} (${calculateAge(birthDate)} ans)`}</p>
      }
      <p className="text-sm font-light">{person.email}</p>
    </>
  }, [person, mapIcon, isMan, getCivility]);

  const handleDeleteClick = useCallback(() => {
    setIsDeleteConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    onDelete?.(person.id);
  }, [onDelete, person.id]);

  return (
    <>
      <GenericCardInfos
        className="grow"
        headerAction={
          <Button
            color="slate"
            round
            onClick={() => onUpdate(person.id)}
          >
            <PenIcon className="text-black" />
          </Button>
        }
        footerAction={
          onDelete && (
            <button
              type="button"
              className="p-1 text-gray-400 hover:text-red-500 transition-colors"
              onClick={handleDeleteClick}
            >
              <TrashIcon className="w-5 h-5" />
            </button>
          )
        }
      >
        {renderBody}
      </GenericCardInfos>

      <ConfirmationOverlay
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="COMMON.DELETE_CONFIRMATION_TITLE"
        description="COMMON.DELETE_CONFIRMATION_DESCRIPTION"
        confirmLabel="COMMON.DELETE"
        variant="danger"
      />
    </>
  );
};
